import React from 'react';
import { MessageSquareHeart } from 'lucide-react';

/**
 * Top-bar entry to the feedback panel — small, quiet, and always the same.
 *
 * Deliberately badge-free and prompt-free: a "give feedback" control that
 * nags is a control people learn to ignore. It sits and waits.
 *
 * Two tones, same control: `surface` for the white top bar, `chrome` for the
 * wizard's navy header. Only the colours differ — size, copy and icon are the
 * same everywhere, so it reads as one thing wherever somebody meets it.
 */
export interface FeedbackButtonProps {
  onClick: () => void;
  /** Which bar it sits in. Default `surface` (the white top bar). */
  tone?: 'surface' | 'chrome';
  className?: string;
}

const TONES: Record<NonNullable<FeedbackButtonProps['tone']>, string> = {
  surface: 'border-line-strong text-ink-secondary hover:bg-surface-hover hover:text-ink focus-visible:ring-action',
  chrome: 'border-chrome-border text-chrome-text hover:bg-white/[0.08] hover:text-white focus-visible:ring-white/60',
};

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ onClick, tone = 'surface', className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    title="Give feedback"
    className={`inline-flex h-[34px] shrink-0 items-center gap-s2 whitespace-nowrap rounded-control border px-s3 text-body-sm font-medium focus:outline-none focus-visible:ring-2 ${TONES[tone]} ${className}`}
  >
    <MessageSquareHeart className="h-4 w-4" />
    <span className="hidden sm:inline">Feedback</span>
  </button>
);

export default FeedbackButton;
